// A plain four-function calculator, deliberately not precedence-aware (2 + 3 × 4 = 20,
// same as pressing those keys on a physical calculator) — this feeds a money amount
// field, not a general expression evaluator, so "what you'd expect from a calculator
// app" beats "what a scientific calculator would do."

export type CalcOperator = '+' | '-' | '×' | '÷'

export interface CalculatorState {
  /** What's currently being typed for the active operand. Empty string reads as "0". */
  display: string
  /** The operand carried over from before the last operator was pressed. */
  previousValue: number | null
  /** The operator waiting to be applied once the next operand is entered. */
  pendingOp: CalcOperator | null
  /** True right after "=" — the next digit starts a fresh number instead of appending. */
  justEvaluated: boolean
  /** Set when a division by zero (or other invalid op) was evaluated. */
  error: boolean
}

export const INITIAL_CALCULATOR_STATE: CalculatorState = {
  display: '',
  previousValue: null,
  pendingOp: null,
  justEvaluated: false,
  error: false,
}

// Rounds to 2dp and trims float noise (0.1 + 0.2 -> 0.3, not 0.30000000000000004) —
// the display only ever needs to read as an amount of money.
function roundMoney(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

function compute(a: number, op: CalcOperator, b: number): number {
  switch (op) {
    case '+':
      return a + b
    case '-':
      return a - b
    case '×':
      return a * b
    case '÷':
      return a / b // Infinity/NaN on b === 0, handled by the caller
  }
}

export function displayValue(state: CalculatorState): string {
  if (state.error) return 'Error'
  return state.display === '' ? '0' : state.display
}

export function pressDigit(state: CalculatorState, digit: string): CalculatorState {
  if (state.error || state.justEvaluated) {
    return { ...INITIAL_CALCULATOR_STATE, display: digit === '0' ? '0' : digit }
  }
  if (state.display === '0') {
    return { ...state, display: digit }
  }
  return { ...state, display: state.display + digit }
}

export function pressDecimal(state: CalculatorState): CalculatorState {
  if (state.error || state.justEvaluated) {
    return { ...INITIAL_CALCULATOR_STATE, display: '0.' }
  }
  if (state.display.includes('.')) return state
  return { ...state, display: (state.display || '0') + '.' }
}

export function pressOperator(state: CalculatorState, op: CalcOperator): CalculatorState {
  if (state.error) return state

  const current = parseFloat(state.display || String(state.previousValue ?? 0))

  if (state.previousValue !== null && state.pendingOp !== null && !state.justEvaluated) {
    const result = compute(state.previousValue, state.pendingOp, current)
    if (!Number.isFinite(result)) {
      return { ...INITIAL_CALCULATOR_STATE, error: true }
    }
    return {
      display: '',
      previousValue: roundMoney(result),
      pendingOp: op,
      justEvaluated: false,
      error: false,
    }
  }

  return {
    display: '',
    previousValue: current,
    pendingOp: op,
    justEvaluated: false,
    error: false,
  }
}

export function pressEquals(state: CalculatorState): CalculatorState {
  if (state.error) return state
  if (state.pendingOp === null || state.previousValue === null) {
    // Nothing to compute yet — "confirm" whatever's on display as-is.
    return { ...state, justEvaluated: true }
  }

  const current = parseFloat(state.display || String(state.previousValue))
  const result = compute(state.previousValue, state.pendingOp, current)
  if (!Number.isFinite(result)) {
    return { ...INITIAL_CALCULATOR_STATE, error: true }
  }

  return {
    display: String(roundMoney(result)),
    previousValue: null,
    pendingOp: null,
    justEvaluated: true,
    error: false,
  }
}

export function pressBackspace(state: CalculatorState): CalculatorState {
  if (state.error) return INITIAL_CALCULATOR_STATE
  return { ...state, display: state.display.slice(0, -1) }
}

export function pressClear(): CalculatorState {
  return INITIAL_CALCULATOR_STATE
}
